import axios from "axios";
import { marked } from "marked";
import DOMPurify from "dompurify";
import "../../scss/assistant.scss";

// Model replies come back as Markdown. GFM enables tables; breaks turns single
// newlines into <br> so chat-style line breaks survive.
marked.setOptions({ gfm: true, breaks: true });

// sessionStorage key for the per-tab active chat: a conversation id, or NEW_CHAT after the user
// explicitly started a fresh chat — so a page load doesn't pull the previous conversation back up.
const ACTIVE_CONVERSATION_KEY = "assistant.activeConversation";
const NEW_CHAT = "new";

/**
 * Assistant drives the dashboard AI chat panel.
 *
 * It is scoped to a single root element (#assistant-panel). Conversations are persisted server-side:
 * sending a message stores it plus a pending answer, then a fire-and-forget call kicks off generation
 * that finishes even if the user navigates away. The panel polls for the answer and, on every page
 * load, reloads the active conversation (resuming the poll if an answer is still being generated). A
 * history list lets the user open and continue older chats.
 *
 * Which conversation counts as active is remembered per tab (sessionStorage): the last one opened or
 * written to, or an explicit "new chat" — the server's pick is only a fallback for fresh tabs.
 */
export class Assistant {
  constructor(panelEl) {
    this.panel = panelEl;
    this.sendUrl = panelEl.dataset.sendUrl;
    this.generateUrl = panelEl.dataset.generateUrl;
    this.pollUrl = panelEl.dataset.pollUrl;
    this.conversationsUrl = panelEl.dataset.conversationsUrl;
    this.conversationUrl = panelEl.dataset.conversationUrl;
    this.exportUrl = panelEl.dataset.exportUrl;
    this.staticMapUrl = panelEl.dataset.staticMapUrl;
    this.messagesEl = panelEl.querySelector("#assistant-messages");
    this.typingEl = panelEl.querySelector("#assistant-typing");
    this.form = panelEl.querySelector("#assistant-form");
    this.input = panelEl.querySelector("#assistant-text");
    this.sendBtn = panelEl.querySelector("#assistant-send");
    this.historyEl = panelEl.querySelector("#assistant-history");
    this.historyToggle = panelEl.querySelector("#assistant-history-toggle");
    this.newChatBtn = panelEl.querySelector("#assistant-new");

    this.conversationId = null;
    this.lastSeenId = 0; // highest message id already rendered; used as poll cursor
    this.lastUserText = ""; // question backing the next answer's PDF export
    this.busy = false;
    this.pollTimer = null;
    this.POLL_INTERVAL_MS = 1500;

    axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
    axios.defaults.headers.common["Content-Type"] = "application/json";
    axios.defaults.headers.common["X-CSRF-Token"] = panelEl.dataset.csrfToken;

    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.send();
    });

    // Submit on Enter, newline on Shift+Enter.
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.send();
      }
    });

    if (this.newChatBtn) {
      this.newChatBtn.addEventListener("click", () => this.newChat());
    }
    if (this.historyToggle && this.historyEl) {
      this.historyToggle.addEventListener("click", () => {
        this.historyEl.classList.toggle("d-none");
      });
    }

    this.init();
  }

  /**
   * On load: fetch the conversation list and open the active conversation, resuming the poll if its
   * answer is still pending. The tab's remembered choice wins — including an explicit new (empty)
   * chat; the server's activeId (the conversation still generating, else the most recent) is only
   * the fallback when this tab has no usable memory of its own.
   *
   * A conversation is only auto-resumed if it was active within the server's resume window
   * (default 6h); after that the panel starts a fresh chat. Deliberately opening an old chat from
   * the history list is unaffected — and so is typing into a chat that is already on screen in a
   * long-open tab, which is treated as the same deliberate choice.
   */
  init() {
    if (!this.conversationsUrl) {
      return; // endpoints not configured (e.g. guest) — leave the panel inert.
    }
    axios
      .get(this.conversationsUrl)
      .then((response) => {
        const data = response.data || {};
        const conversations = data.conversations || [];
        this.renderConversationList(conversations);

        // Server time is used so a wrong client clock can't defeat (or over-trigger) the window.
        const windowSecs = data.resumeWindowSeconds || 0;
        const now = data.now || Math.floor(Date.now() / 1000);
        const isFresh = (conv) => !windowSecs || now - (conv.updatedAt || 0) < windowSecs;

        const remembered = this.recallConversation();
        if (remembered === NEW_CHAT) {
          return; // the user deliberately started a fresh chat — keep the panel empty.
        }
        const rememberedId = parseInt(remembered, 10);
        const rememberedConv = rememberedId
          ? conversations.find((conv) => conv.id === rememberedId)
          : null;
        if (rememberedConv && isFresh(rememberedConv)) {
          this.loadConversation(rememberedId);
        } else if (data.activeId) {
          // activeId is already window-filtered server-side (pending conversations excepted).
          this.loadConversation(data.activeId);
        }
      })
      .catch(() => {
        /* a failed history load must not break the panel; the user can still start a new chat. */
      });
  }

  /**
   * Remembers this tab's active chat (a conversation id or NEW_CHAT) across page loads.
   * Storage failures (e.g. private browsing) are ignored — the server fallback still works.
   *
   * @param {number|string} value
   */
  rememberConversation(value) {
    try {
      sessionStorage.setItem(ACTIVE_CONVERSATION_KEY, String(value));
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * @returns {string|null} the remembered value, or null if none/unavailable.
   */
  recallConversation() {
    try {
      return sessionStorage.getItem(ACTIVE_CONVERSATION_KEY);
    } catch (e) {
      return null;
    }
  }

  /**
   * Renders the clickable list of past conversations.
   *
   * @param {Array<{id:number,title:string,updatedAt:number}>} conversations
   */
  renderConversationList(conversations) {
    if (!this.historyEl) {
      return;
    }
    this.historyEl.innerHTML = "";
    conversations.forEach((conv) => {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "assistant-history__item";
      item.textContent = conv.title || "Chat";
      if (conv.id === this.conversationId) {
        item.classList.add("assistant-history__item--active");
      }
      item.addEventListener("click", () => {
        this.loadConversation(conv.id);
        this.historyEl.classList.add("d-none");
      });
      this.historyEl.appendChild(item);
    });
  }

  /**
   * Re-fetches only the conversation list (e.g. to pick up a freshly generated title) without
   * touching the open conversation.
   */
  refreshConversationList() {
    if (!this.conversationsUrl) {
      return;
    }
    axios
      .get(this.conversationsUrl)
      .then((response) => this.renderConversationList((response.data || {}).conversations || []))
      .catch(() => {});
  }

  /**
   * Loads and renders an existing conversation, resuming the poll if its last answer is still pending.
   *
   * @param {number} id
   */
  loadConversation(id) {
    this.stopPolling();
    axios
      .get(this.conversationUrl, { params: { id: id } })
      .then((response) => {
        const data = response.data || {};
        if (!data.ok) {
          return;
        }
        this.conversationId = data.conversationId;
        this.rememberConversation(data.conversationId);
        this.messagesEl.innerHTML = "";
        this.lastSeenId = 0;
        this.lastUserText = "";
        let pending = false;

        (data.messages || []).forEach((msg) => {
          if (msg.role === "assistant" && msg.status === "pending") {
            pending = true;
            return; // answer not ready yet — the poll will render it.
          }
          this.renderStoredMessage(msg, false);
          this.lastSeenId = Math.max(this.lastSeenId, msg.id);
        });

        if (pending) {
          this.setBusy(true);
          this.startPolling();
          // Ask the widget to open (compact) so a generation in progress is visible after navigation.
          this.panel.dispatchEvent(new CustomEvent("assistant:active"));
        }
      })
      .catch(() => {});
  }

  /**
   * Renders one stored message (from a loaded conversation or a poll result) as the right bubble.
   *
   * @param {{role:string,content:string,status:string,toolsUsed:string[],error:string}} msg
   * @param {boolean} live Whether the message just arrived (poll) as opposed to being replayed from
   *   history — replayed messages must not re-execute page-changing actions (see executeUiFilters).
   */
  renderStoredMessage(msg, live) {
    if (msg.role === "user") {
      this.lastUserText = msg.content || "";
      this.appendMessage("user", msg.content || "");
    } else if (msg.status === "error") {
      this.appendMessage("error", msg.error || "Something went wrong.");
    } else {
      this.appendMessage("assistant", msg.content || "", msg.toolsUsed, this.lastUserText, live);
    }
  }

  /**
   * Starts a fresh, empty conversation.
   */
  newChat() {
    this.stopPolling();
    this.conversationId = null;
    this.rememberConversation(NEW_CHAT);
    this.lastSeenId = 0;
    this.lastUserText = "";
    this.messagesEl.innerHTML = "";
    this.setBusy(false);
    this.input.focus();
    if (this.historyEl) {
      this.historyEl.classList.add("d-none");
    }
  }

  send() {
    const text = this.input.value.trim();
    if (!text || this.busy) {
      return;
    }

    this.input.value = "";
    this.lastUserText = text;
    this.appendMessage("user", text);
    this.setBusy(true);

    axios
      .post(this.sendUrl, { conversationId: this.conversationId, message: text })
      .then((response) => {
        const data = response.data || {};
        if (!data.ok) {
          this.appendMessage("error", data.error || "Something went wrong.");
          this.setBusy(false);
          return;
        }
        this.conversationId = data.conversationId;
        this.rememberConversation(data.conversationId);
        // The user bubble is already on screen; advance the poll cursor past it so polling only
        // surfaces the assistant answer (and never re-renders this question).
        this.lastSeenId = data.userMessageId;

        // Fire-and-forget: the server runs generation to completion even if this request is dropped
        // (page navigation) or the answer takes a while. We never wait on it — the poll delivers the
        // result, here or after a reload on any page.
        axios
          .post(this.generateUrl, { messageId: data.assistantMessageId, page: this.detectPage() })
          .catch(() => {});

        this.startPolling();
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        this.appendMessage("error", (data && data.error) || "Request failed. Please try again.");
        this.setBusy(false);
      });
  }

  /**
   * Polls for messages newer than the cursor until the pending answer arrives (done/error).
   */
  startPolling() {
    this.stopPolling();
    if (!this.conversationId) {
      return;
    }
    const tick = () => {
      axios
        .get(this.pollUrl, { params: { conversationId: this.conversationId, afterId: this.lastSeenId } })
        .then((response) => this.handlePolled((response.data || {}).messages || []))
        .catch(() => {
          /* transient poll failure — keep polling; a persistent one stops via the stale timeout. */
        });
    };
    this.pollTimer = setInterval(tick, this.POLL_INTERVAL_MS);
    tick();
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Handles a batch of polled messages: renders completed turns, keeps waiting on a pending answer,
   * and stops once the answer is delivered.
   *
   * @param {Array} messages
   */
  handlePolled(messages) {
    messages.forEach((msg) => {
      if (msg.id <= this.lastSeenId) {
        return;
      }
      if (msg.role === "assistant" && msg.status === "pending") {
        return; // still generating — leave the cursor so we re-check this message next tick.
      }
      this.renderStoredMessage(msg, true);
      this.lastSeenId = msg.id;
      if (msg.role === "assistant") {
        // Answer delivered (or errored): stop waiting and refresh the list to pick up a new title.
        this.stopPolling();
        this.setBusy(false);
        this.refreshConversationList();
      }
    });
  }

  appendMessage(role, text, toolsUsed, question, live) {
    const msg = document.createElement("div");
    msg.className = "assistant-msg assistant-msg--" + role;

    const body = document.createElement("div");
    body.className = "assistant-msg__body";
    if (role === "assistant") {
      // Render the model's Markdown, then sanitize before injecting — innerHTML is
      // only ever fed DOMPurify output, so model/tool content can't inject scripts.
      body.classList.add("assistant-msg__body--md");
      body.innerHTML = DOMPurify.sanitize(marked.parse(text || ""));
    } else {
      body.textContent = text; // user/error stay plain text — never interpreted as markup.
    }
    msg.appendChild(body);

    if (toolsUsed && toolsUsed.length) {
      const tools = document.createElement("div");
      tools.className = "assistant-msg__tools";
      tools.textContent = "Tools: " + toolsUsed.join(", ");
      msg.appendChild(tools);
    }

    this.messagesEl.appendChild(msg);

    // Charts must be rendered only after the message is in the DOM: Chart.js sizes the canvas from
    // its laid-out parent, so a detached node would render at 0×0 (blank chart, empty PNG export).
    if (role === "assistant") {
      this.renderCharts(body);
      this.executeMapActions(body, live !== false);
      this.executeUiFilters(body, live !== false);
      this.appendExportAction(msg, body, question || "");
    }

    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  /**
   * Adds a per-answer "export as PDF" action row below an assistant message. The button hands the
   * rendered body (charts already on the DOM as canvases) to exportPdf().
   *
   * @param {HTMLElement} msg The .assistant-msg wrapper.
   * @param {HTMLElement} body The rendered markdown body.
   * @param {string} question The user question that produced this answer (used as the PDF title basis).
   */
  appendExportAction(msg, body, question) {
    if (!this.exportUrl) {
      return; // export endpoint not configured; skip the button entirely.
    }
    const actions = document.createElement("div");
    actions.className = "assistant-msg__actions";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "assistant-msg__btn";
    btn.textContent = "Als PDF exportieren";
    btn.addEventListener("click", () => {
      this.exportPdf(body, question, btn);
    });

    actions.appendChild(btn);
    msg.appendChild(actions);
  }

  /**
   * Exports a single assistant answer to PDF. Chart <canvas> elements can't be serialised as HTML, so
   * each is read as a PNG data URL (from the live, rendered canvas) and swapped for an <img> in a
   * clone of the body. The cleaned HTML is posted to the server, which renders the multi-page PDF
   * (styled header/footer) and streams it back as a download.
   *
   * @param {HTMLElement} body
   * @param {string} question
   * @param {HTMLButtonElement} btn
   */
  exportPdf(body, question, btn) {
    if (btn.disabled) {
      return;
    }
    const flash = (label) => {
      const prev = btn.dataset.label || btn.textContent;
      btn.dataset.label = prev;
      btn.textContent = label;
    };
    const reset = () => {
      btn.disabled = false;
      btn.textContent = btn.dataset.label || "Als PDF exportieren";
    };

    // Capture chart PNGs from the LIVE canvases before cloning — a cloned <canvas> has no bitmap.
    const charts = body.querySelectorAll(".assistant-chart");
    const images = [];
    charts.forEach((wrap) => {
      const canvas = wrap.querySelector("canvas");
      images.push(canvas ? canvas.toDataURL("image/png", 1) : null);
    });

    const clone = body.cloneNode(true);
    // Replace each chart wrapper with its captured image (or drop it if capture failed).
    clone.querySelectorAll(".assistant-chart").forEach((wrap, i) => {
      const src = images[i];
      if (src) {
        const img = document.createElement("img");
        img.src = src;
        wrap.parentNode.replaceChild(img, wrap);
      } else {
        wrap.parentNode.removeChild(wrap);
      }
    });
    // Strip interactive-only bits that shouldn't appear in the PDF.
    clone.querySelectorAll(".assistant-chart__actions, .assistant-msg__tools").forEach((el) => {
      el.parentNode.removeChild(el);
    });

    btn.disabled = true;
    flash("PDF wird erstellt…");

    axios
      .post(
        this.exportUrl,
        { html: clone.innerHTML, question: question },
        { responseType: "blob" }
      )
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.exportFilename(response);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        reset();
      })
      .catch(() => {
        flash("Export fehlgeschlagen");
        setTimeout(reset, 2000);
      });
  }

  /**
   * Derives the download filename from the response's Content-Disposition header, with a dated fallback.
   *
   * @param {object} response Axios response.
   * @returns {string}
   */
  exportFilename(response) {
    const disposition = (response.headers && response.headers["content-disposition"]) || "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) {
      return match[1];
    }
    return "tapatron.pdf";
  }

  /**
   * Turns ```chart fenced blocks (a Chart.js v3 config as JSON) emitted by the model into rendered
   * canvases with PNG export. The block arrives as <pre><code class="language-chart">{...}</code></pre>
   * (marked's output, already DOMPurify-sanitized). We only ever read its text content and feed it
   * to JSON.parse, so no markup from the model is interpreted. Anything that fails to parse or
   * validate is left as the original code block (graceful fallback).
   *
   * @param {HTMLElement} body
   */
  renderCharts(body) {
    if (!window.Chart) {
      return; // Chart.js global not loaded; leave the raw config block visible.
    }

    const ALLOWED_TYPES = ["bar", "line", "pie", "doughnut"];
    // Models don't reliably tag the fence as ```chart, so we inspect every code block and only
    // treat the ones that parse to a valid Chart.js config (allowed type + data) as charts.
    const blocks = body.querySelectorAll("pre > code");

    blocks.forEach((codeEl) => {
      let config;
      try {
        config = JSON.parse(codeEl.textContent || "");
      } catch (e) {
        return; // not valid JSON — keep the code block as-is.
      }

      if (!config || typeof config !== "object" || ALLOWED_TYPES.indexOf(config.type) === -1 || !config.data) {
        return;
      }

      // Drop function-like string values (e.g. tooltip callbacks the model emits as
      // "function(ctx){...}") — JSON can't carry functions, and Chart.js would throw trying to
      // call the string. Stripping them keeps the rest of the chart renderable.
      this.stripFunctionStrings(config);

      // Force responsive sizing so the chart fits the fixed-height wrapper in the narrow panel.
      config.options = config.options || {};
      config.options.responsive = true;
      config.options.maintainAspectRatio = false;

      const wrap = document.createElement("div");
      wrap.className = "assistant-chart";

      const canvasWrap = document.createElement("div");
      canvasWrap.className = "assistant-chart__canvas-wrap";
      const canvas = document.createElement("canvas");
      canvasWrap.appendChild(canvas);
      wrap.appendChild(canvasWrap);

      const actions = document.createElement("div");
      actions.className = "assistant-chart__actions";
      const downloadBtn = document.createElement("button");
      downloadBtn.type = "button";
      downloadBtn.className = "assistant-chart__btn";
      downloadBtn.textContent = "Download PNG";
      const copyBtn = document.createElement("button");
      copyBtn.type = "button";
      copyBtn.className = "assistant-chart__btn";
      copyBtn.textContent = "Copy";
      actions.appendChild(downloadBtn);
      actions.appendChild(copyBtn);
      wrap.appendChild(actions);

      const pre = codeEl.parentNode;
      pre.parentNode.replaceChild(wrap, pre);

      let chart;
      try {
        chart = new window.Chart(canvas.getContext("2d"), config);
      } catch (e) {
        // Bad config Chart.js rejected — restore the original block so nothing is silently lost.
        wrap.parentNode.replaceChild(pre, wrap);
        return;
      }

      downloadBtn.addEventListener("click", () => {
        const a = document.createElement("a");
        a.href = chart.toBase64Image("image/png", 1);
        a.download = "chart.png";
        a.click();
      });

      copyBtn.addEventListener("click", () => {
        this.copyChart(canvas, copyBtn);
      });
    });
  }

  /**
   * Recursively deletes string values that look like JavaScript functions (e.g. tooltip
   * callbacks the model serialises as "function(ctx){...}" or "(ctx) => ..."). They cannot work
   * as data and would make Chart.js throw when it tries to invoke the string.
   *
   * @param {*} node
   */
  stripFunctionStrings(node) {
    if (Array.isArray(node)) {
      node.forEach((item) => this.stripFunctionStrings(item));
      return;
    }
    if (!node || typeof node !== "object") {
      return;
    }
    const FN_RE = /^\s*(function\b|async\b|\(?[\w\s,]*\)?\s*=>)/;
    Object.keys(node).forEach((key) => {
      const value = node[key];
      if (typeof value === "string" && FN_RE.test(value)) {
        delete node[key];
      } else if (value && typeof value === "object") {
        this.stripFunctionStrings(value);
      }
    });
  }

  /**
   * Executes ```map-action fenced blocks emitted by the model against the map control facade
   * (window.tapaMap). The block arrives as <pre><code>{...}</code></pre> (marked output, already
   * DOMPurify-sanitized); we only ever read its text and JSON.parse it, so no model markup is
   * interpreted. The content is either a single action object or an array of them, each shaped like
   * {"action": "setLayer", ...}.
   *
   * Only whitelisted actions are dispatched; the facade itself validates the arguments. Recognised
   * blocks are removed from the chat (they are commands, not content) and replaced with a short
   * status line. If the map is not on the current page (the assistant is global, the map is not),
   * a friendly hint is shown instead. Stored messages replayed from the conversation history
   * (live === false) are NOT executed: replaying every past route/resilience action on each page
   * load re-drew old routes (the async resilience plan resolved last and painted over the current
   * standard route). The block is still removed and replaced by a status line.
   *
   * @param {HTMLElement} body
   * @param {boolean} [live=true]
   */
  executeMapActions(body, live = true) {
    const ALLOWED = [
      "setLayer",
      "setHeatmap",
      "setTraffic",
      "planRoute",
      "clearRoute",
      "setRadius",
      "planResilienceRoute",
      "clearResilienceRoutes",
      "assessRoute",
      "clearRouteAssessment",
    ];
    const blocks = body.querySelectorAll("pre > code");

    blocks.forEach((codeEl) => {
      let parsed;
      try {
        parsed = JSON.parse(codeEl.textContent || "");
      } catch (e) {
        return; // not JSON — leave the block alone (e.g. a normal code sample).
      }

      const actions = Array.isArray(parsed) ? parsed : [parsed];
      // Only treat this as a map-action block if every entry is a recognised action. This keeps us
      // from swallowing unrelated JSON code blocks (and chart configs, handled separately).
      const isMapActions =
        actions.length > 0 &&
        actions.every((a) => a && typeof a === "object" && ALLOWED.indexOf(a.action) !== -1);
      if (!isMapActions) {
        return;
      }

      const pre = codeEl.parentNode;
      pre.parentNode.removeChild(pre);

      if (!live) {
        this.appendMapNote(body, "Map action from an earlier turn (not re-applied): " + actions.map((a) => a.action).join(", "));
        return;
      }

      if (!window.tapaMap) {
        this.appendMapNote(body, "Diese Aktion steuert die Karte und funktioniert nur auf der Kartenseite.");
        return;
      }

      this.runMapActionsSequentially(actions, body);
    });
  }

  /**
   * Runs a map-action block one action at a time. planRoute, planResilienceRoute and assessRoute
   * return promises (Google/route-risk requests): awaiting them keeps a block such as
   * [planRoute, assessRoute] correct — the assessment must see the newly drawn route, not the
   * previous one (or none). A failed action (false) is skipped, the rest of the block still runs.
   *
   * @param {object[]} actions
   * @param {HTMLElement} body
   * @returns {Promise<void>}
   */
  async runMapActionsSequentially(actions, body) {
    const done = [];
    for (const a of actions) {
      let result;
      try {
        result = await Promise.resolve(this.runMapAction(a));
      } catch (e) {
        result = false;
      }
      if (result) {
        done.push(a.action);
      }
    }
    if (done.length) {
      this.appendMapNote(body, "Karte aktualisiert: " + done.join(", "));
    }

    // A drawn standard route gets a small static PNG preview inline (and thus in the PDF export).
    if (this.staticMapUrl && window.tapaMap && done.indexOf("planRoute") !== -1) {
      this.generateMapPreview(body);
    }
  }

  /**
   * Executes ```ui-filter fenced blocks ({"action":"setIncidentFilters","filters":{...}}) emitted by
   * the model: applies the requested incident filters through the filter form of the page the user is
   * currently on (interactive map or Cargo Crime Monitor). Like map-action blocks, the JSON is a
   * command, not content — recognised blocks are removed from the chat and replaced with a status
   * note; on pages without a filter form a hint is shown instead.
   *
   * `live` is false when the message is replayed from history (loadConversation). Replayed blocks are
   * only stripped, never applied: applying on the Cargo Crime Monitor submits its filter form, which
   * reloads the page, which replays the conversation — re-executing here would loop forever.
   *
   * @param {HTMLElement} body
   * @param {boolean} live
   */
  executeUiFilters(body, live) {
    const blocks = body.querySelectorAll("pre > code");

    blocks.forEach((codeEl) => {
      let parsed;
      try {
        parsed = JSON.parse(codeEl.textContent || "");
      } catch (e) {
        return; // not JSON — leave the block alone (e.g. a normal code sample).
      }
      if (
        !parsed ||
        typeof parsed !== "object" ||
        Array.isArray(parsed) ||
        parsed.action !== "setIncidentFilters" ||
        !parsed.filters ||
        typeof parsed.filters !== "object"
      ) {
        return;
      }

      const pre = codeEl.parentNode;
      pre.parentNode.removeChild(pre);

      if (!live) {
        this.appendMapNote(body, "Filter wurde in dieser Antwort gesetzt.");
        return;
      }

      const filters = this.sanitizeFilters(parsed.filters);
      if (!Object.keys(filters).length) {
        return;
      }

      if (document.getElementById("incident-filter-form")) {
        this.applyMapFilters(filters, body);
      } else if (document.getElementById("ccm-filter-form")) {
        this.applyCcmFilters(filters, body);
      } else {
        this.appendMapNote(body, "Dieser Filter funktioniert nur auf der Karten- oder Cargo-Crime-Monitor-Seite.");
      }
    });
  }

  /**
   * Whitelists and type-checks a raw filters object from the model. Id lists are coerced to positive
   * integers (an explicitly empty array survives — it means "clear this filter"), dates must be
   * dd.mm.yyyy strings. Unknown keys are dropped.
   *
   * @param {object} raw
   * @returns {object} canonical filters; empty object when nothing valid remains.
   */
  sanitizeFilters(raw) {
    const ID_KEYS = [
      "product_category_ids",
      "category_ids",
      "modus_operandi_ids",
      "location_type_ids",
      "country_ids",
      "region_ids",
    ];
    const DATE_RE = /^\d{2}\.\d{2}\.\d{4}$/;
    const out = {};

    ID_KEYS.forEach((key) => {
      if (!Array.isArray(raw[key])) {
        return;
      }
      const ids = raw[key].map((v) => parseInt(v, 10)).filter((v) => Number.isInteger(v) && v > 0);
      if (ids.length || raw[key].length === 0) {
        out[key] = ids;
      }
    });
    ["date_from", "date_to"].forEach((key) => {
      if (typeof raw[key] === "string" && DATE_RE.test(raw[key])) {
        out[key] = raw[key];
      }
    });
    if (typeof raw.high_value === "boolean") {
      out.high_value = raw.high_value;
    }
    if (raw.incident_type === "occurred" || raw.incident_type === "reported") {
      out.incident_type = raw.incident_type;
    }
    return out;
  }

  /**
   * Human-readable label for a canonical filter key (used in the status notes).
   *
   * @param {string} key
   * @returns {string}
   */
  filterLabel(key) {
    const labels = {
      product_category_ids: "Produktkategorie",
      category_ids: "Kategorie",
      modus_operandi_ids: "Modus Operandi",
      location_type_ids: "Location Type",
      country_ids: "Land",
      region_ids: "Region",
      date_from: "Datum von",
      date_to: "Datum bis",
      high_value: "High Value",
      incident_type: "Incident-Typ",
    };
    return labels[key] || key;
  }

  /**
   * Applies canonical filters on the interactive map page by filling the inline-rendered filter
   * modal form and clicking its Apply button. That reuses the existing AJAX path from
   * incident/_search.php: POST /map/filter (CSRF included), reloadData event for the incidents
   * layer, and a fresh server render of the modal — fields not mentioned keep their current
   * session values (merge semantics).
   *
   * @param {object} filters sanitized canonical filters
   * @param {HTMLElement} body
   */
  applyMapFilters(filters, body) {
    const $ = window.jQuery;
    if (!$) {
      return;
    }
    const MULTI = {
      product_category_ids: "#incidentsearch-incident_product_category_ids",
      category_ids: "#incidentsearch-incident_category_ids",
      modus_operandi_ids: "#incidentsearch-incident_modus_operandi_ids",
      location_type_ids: "#incidentsearch-incident_location_type_ids",
      country_ids: "#incidentsearch-config_country_ids",
      region_ids: "#incidentsearch-config_tapa_region_ids",
    };
    const applied = [];
    const skipped = [];

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (MULTI[key]) {
        $(MULTI[key]).val(value.map(String)).trigger("change");
        applied.push(this.filterLabel(key));
      } else if (key === "date_from") {
        $("#incidentsearch-incident_date_from_formatted").val(value).trigger("change");
        applied.push(this.filterLabel(key));
      } else if (key === "date_to") {
        $("#incidentsearch-incident_date_to_formatted").val(value).trigger("change");
        applied.push(this.filterLabel(key));
      } else if (key === "high_value") {
        $("#incidentsearch-high_value").val(value ? "1" : "0").trigger("change");
        applied.push(this.filterLabel(key));
      } else {
        skipped.push(this.filterLabel(key)); // incident_type — Cargo Crime Monitor only.
      }
    });

    if (applied.length) {
      $("#incident-filter-btn").trigger("click");
    }
    let note = applied.length ? "Kartenfilter angewendet: " + applied.join(", ") : "";
    if (skipped.length) {
      note += (note ? " — " : "") + "auf der Karte nicht verfügbar: " + skipped.join(", ");
    }
    if (note) {
      this.appendMapNote(body, note);
    }
  }

  /**
   * Applies canonical filters on the Cargo Crime Monitor page by filling its filter form and
   * submitting it — a classic POST, so the page reloads. The status note is appended first and the
   * submit deferred a moment so the poll bookkeeping of this message finishes before navigation;
   * after the reload the widget reopens and the conversation replays (the block is then only
   * stripped, see executeUiFilters).
   *
   * @param {object} filters sanitized canonical filters
   * @param {HTMLElement} body
   */
  applyCcmFilters(filters, body) {
    const $ = window.jQuery;
    if (!$) {
      return;
    }
    const MULTI = {
      product_category_ids: "#ccm-incident-product-category-select",
      category_ids: "#ccm-incident-category-select",
      modus_operandi_ids: "#ccm-incident-modus-operandi-select",
      location_type_ids: "#ccm-incident-location-type-select",
    };
    const applied = [];
    const skipped = [];
    // Countries and TAPA regions share one select on this page; regions use "r_<id>" tokens.
    const countryTokens = [];

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (MULTI[key]) {
        $(MULTI[key]).val(value.map(String)).trigger("change");
        applied.push(this.filterLabel(key));
      } else if (key === "country_ids") {
        value.forEach((id) => countryTokens.push(String(id)));
        applied.push(this.filterLabel(key));
      } else if (key === "region_ids") {
        value.forEach((id) => countryTokens.push("r_" + id));
        applied.push(this.filterLabel(key));
      } else if (key === "date_from" || key === "date_to") {
        $('input[name="' + key + '"]').val(value).trigger("change");
        applied.push(this.filterLabel(key));
      } else if (key === "incident_type") {
        $("#ccm-incident-type-select").val(value === "reported" ? "2" : "1").trigger("change");
        applied.push(this.filterLabel(key));
      } else {
        skipped.push(this.filterLabel(key)); // high_value — map only.
      }
    });

    if ("country_ids" in filters || "region_ids" in filters) {
      $("#ccm-countries-select").val(countryTokens).trigger("change");
    }

    if (!applied.length) {
      if (skipped.length) {
        this.appendMapNote(body, "Im Cargo Crime Monitor nicht verfügbar: " + skipped.join(", "));
      }
      return;
    }
    let note = "Filter angewendet: " + applied.join(", ") + " — Seite wird neu geladen …";
    if (skipped.length) {
      note += " (nicht verfügbar hier: " + skipped.join(", ") + ")";
    }
    this.appendMapNote(body, note);
    setTimeout(() => {
      $("#ccm-filter-form").trigger("submit");
    }, 150);
  }

  /**
   * Identifies the filterable page the user is on. Sent as a hint with the generate request so the
   * model knows whether emitting a ui-filter block makes sense (and with which page's vocabulary).
   *
   * @returns {string|null}
   */
  detectPage() {
    if (document.getElementById("incident-filter-form")) {
      return "map";
    }
    if (document.getElementById("ccm-filter-form")) {
      return "cargo-crime-monitor";
    }
    return null;
  }

  /**
   * Renders a small static map preview of the just-planned route and appends it to the message body.
   *
   * planRoute draws the route asynchronously (the DirectionsService callback populates
   * window.directionsRenderer a moment after runMapAction returns), so we wait for the route geometry
   * to become available before requesting the preview. The PNG is produced by a server-side proxy
   * (assistant/static-map) that returns a base64 data URI — which then rides into the PDF export
   * exactly like the chart PNGs. The whole flow is best-effort: any failure leaves the chat untouched.
   *
   * @param {HTMLElement} body The rendered markdown body to append the preview to.
   */
  generateMapPreview(body) {
    const TIMEOUT_MS = 8000;
    const POLL_MS = 250;
    const start = Date.now();

    const tryOnce = () => {
      const geo = window.tapaMap && window.tapaMap.getRouteGeometry && window.tapaMap.getRouteGeometry();
      if (!geo) {
        if (Date.now() - start < TIMEOUT_MS) {
          setTimeout(tryOnce, POLL_MS);
        }
        return; // no route yet (or none at all) — give up silently after the timeout.
      }
      axios
        .post(this.staticMapUrl, {
          polyline: geo.polyline,
          origin: geo.origin,
          destination: geo.destination,
          width: 400,
          height: 240,
          scale: 2,
        })
        .then((res) => {
          const data = res.data || {};
          if (data.ok && data.dataUri) {
            this.appendMapPreview(body, data.dataUri, geo);
          }
        })
        .catch(() => {
          /* preview is optional — never break the chat over it. */
        });
    };

    tryOnce();
  }

  /**
   * Appends a static map preview image (a data: URI PNG) to an assistant message body. The image
   * lives inside `body` (the cloned-and-posted element in exportPdf), so it is carried into the PDF
   * with no special handling — it is already a data-URI <img>, which the export pipeline supports.
   *
   * @param {HTMLElement} body
   * @param {string} dataUri data:image/png;base64,...
   * @param {{originName: string, destinationName: string}} geo
   */
  appendMapPreview(body, dataUri, geo) {
    const route = (geo.originName || "") + " → " + (geo.destinationName || "");

    const fig = document.createElement("figure");
    fig.className = "assistant-map-preview";

    const img = document.createElement("img");
    img.className = "assistant-map-preview__img";
    img.src = dataUri;
    img.alt = "Routenvorschau: " + route;
    fig.appendChild(img);

    const caption = document.createElement("figcaption");
    caption.className = "assistant-map-preview__caption";
    caption.textContent = route;
    fig.appendChild(caption);

    body.appendChild(fig);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  /**
   * Dispatches a single validated action to the map facade. Returns whether it was applied.
   *
   * @param {object} a
   * @returns {boolean}
   */
  runMapAction(a) {
    const map = window.tapaMap;
    switch (a.action) {
      case "setLayer":
        return map.setLayer(a.layer, a.on !== false);
      case "setHeatmap":
        return map.setHeatmap(a.on !== false);
      case "setTraffic":
        return map.setTraffic(a.on !== false);
      case "planRoute":
        return map.planRoute({ origin: a.origin, destination: a.destination, waypoints: a.waypoints });
      case "clearRoute":
        return map.clearRoute();
      case "setRadius":
        return map.setRadius(a.km, a.on !== false);
      case "planResilienceRoute":
        // Internal facade method keeps its name (planSafetyRoute); only the assistant vocabulary is
        // "resilience". Returns a Promise (async planning) — truthy, so it counts as applied.
        return map.planSafetyRoute({ origin: a.origin, destination: a.destination });
      case "clearResilienceRoutes":
        return map.clearSafetyRoutes();
      case "assessRoute":
        // Returns a Promise (async request) — truthy, so it counts as applied.
        return map.assessRoute({
          highValue: a.highValue,
          dataWindowMonths: a.dataWindowMonths,
          departureTime: a.departureTime,
          driverHours: a.driverHours,
        });
      case "clearRouteAssessment":
        return map.clearRouteAssessment();
      default:
        return false;
    }
  }

  /**
   * Appends a small status line below a message body (reuses the tools-badge styling).
   *
   * @param {HTMLElement} body
   * @param {string} text
   */
  appendMapNote(body, text) {
    const note = document.createElement("div");
    note.className = "assistant-msg__tools";
    note.textContent = text;
    body.appendChild(note);
  }

  /**
   * Copies a chart canvas to the clipboard as a PNG, with graceful fallback for browsers that
   * lack the async ClipboardItem image API.
   *
   * @param {HTMLCanvasElement} canvas
   * @param {HTMLButtonElement} btn
   */
  copyChart(canvas, btn) {
    const flash = (label) => {
      const prev = btn.textContent;
      btn.textContent = label;
      setTimeout(() => {
        btn.textContent = prev;
      }, 1500);
    };

    if (!navigator.clipboard || typeof window.ClipboardItem === "undefined" || !canvas.toBlob) {
      flash("Not supported");
      return;
    }

    canvas.toBlob((blob) => {
      if (!blob) {
        flash("Failed");
        return;
      }
      navigator.clipboard
        .write([new window.ClipboardItem({ "image/png": blob })])
        .then(() => flash("Copied!"))
        .catch(() => flash("Failed"));
    }, "image/png");
  }

  setBusy(busy) {
    this.busy = busy;
    this.panel.classList.toggle("assistant-panel--busy", busy);
    this.typingEl.classList.toggle("d-none", !busy);
    if (this.sendBtn) {
      this.sendBtn.disabled = busy;
    }
    if (busy) {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    }
  }
}
